import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES, SECTION } from '../routes.js';
import './Process.css';

const STEPS = [
  {
    title: 'Zone wählen.',
    text: 'Sie sagen uns, wo Sie investieren wollen – Straße, Stadtteil, Insel, Region.',
  },
  {
    title: 'Wir zeigen, was kein anderer sieht.',
    text: 'Jedes verlassene, heruntergekommene, verwucherte, in die Jahre gekommene Gebäude Ihrer Zone – lückenlos erfasst.',
  },
  {
    title: 'Sie picken Ihre Favoriten.',
    text: 'Kurz durchklicken, markieren, fertig.',
  },
  {
    title: 'Wir fahren raus.',
    text: 'Foto- und Videomaterial vor Ort, Umgebungs-Check, Kataster-Daten, Nachbarschaft, Amt. Plus: Wir machen den Eigentümer ausfindig.',
  },
  {
    title: 'Wir klopfen an – in Ihrem Namen.',
    text: 'Briefkasten-Ansprache, Recherche, Gesprächstermin. Sie erscheinen nicht als Bittsteller, sondern als vorbereiteter Investor.',
  },
  {
    title: 'Sie übernehmen den Deal.',
    text: 'Verhandlung, Kauf, Schlüssel. Ihr Projekt, Ihr Gewinn.',
  },
];

/* Kanal-Konfigurationen steuern Labels, Pflichtfelder und UI-Text.
   So bleibt die Form-Komponente kompakt, weitere Kanäle ließen sich leicht ergänzen. */
const CHANNELS = {
  rueckruf: {
    id: 'rueckruf',
    label: 'Rückruf',
    tagline: '15 Min · Telefon',
    titleHtml: (
      <>
        Unverbindlicher <em>Rückruf</em>.
      </>
    ),
    sub: 'Wir melden uns im gewünschten Zeitfenster telefonisch bei Ihnen – diskret, ohne Notiz im Adressbuch.',
    contactLabel: 'Telefon',
    contactPlaceholder: '+43 660 000 00 00',
    timeLabel: 'Wann sollen wir anrufen?',
    timeOptions: [
      'Vormittag (9–12 Uhr)',
      'Mittag (12–14 Uhr)',
      'Nachmittag (14–17 Uhr)',
      'Abend (17–20 Uhr)',
    ],
    submitLabel: 'Rückruf anfordern',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
  whatsapp: {
    id: 'whatsapp',
    label: 'WhatsApp',
    tagline: 'Infos direkt aufs Handy',
    titleHtml: (
      <>
        Per <em>WhatsApp</em> erreichen.
      </>
    ),
    sub: 'Wir schicken Ihnen eine kurze Nachricht mit allen wichtigen Infos – Sie antworten, wann es passt.',
    contactLabel: 'WhatsApp-Nummer',
    contactPlaceholder: '+43 660 000 00 00',
    timeLabel: 'Wann dürfen wir schreiben?',
    timeOptions: [
      'Jetzt – sofort',
      'Heute Abend',
      'Morgen Vormittag',
      'Morgen Nachmittag',
    ],
    submitLabel: 'WhatsApp anfordern',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
    ),
  },
  zoom: {
    id: 'zoom',
    label: 'Zoom-Call',
    tagline: '30 Min · Video',
    titleHtml: (
      <>
        Persönlich per <em>Zoom</em>.
      </>
    ),
    sub: 'Persönliches Video-Gespräch. Wir besprechen Ihre Zone, zeigen Beispiele und klären offene Fragen.',
    contactLabel: 'Telefon (optional)',
    contactPlaceholder: '+43 660 000 00 00',
    /* Zoom bekommt eigene Date+Slot-UI (siehe unten),
       timeOptions dient hier als konkrete Uhrzeit-Slots. */
    timeLabel: 'Uhrzeit',
    timeOptions: [
      '09:00 – 10:30',
      '10:30 – 12:00',
      '13:00 – 14:30',
      '14:30 – 16:00',
      '16:00 – 17:30',
      '18:00 – 19:30',
    ],
    submitLabel: 'Zoom-Call vereinbaren',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
    ),
  },
};

/* Datum-Range für den Zoom-Kalender-Picker: ab morgen, bis +28 Tage.
   Gibt ISO-Strings (YYYY-MM-DD) zurück, die <input type="date"> versteht. */
const isoDate = (d) => d.toISOString().slice(0, 10);
const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);
const fourWeeks = new Date(today);
fourWeeks.setDate(today.getDate() + 28);
const DATE_MIN = isoDate(tomorrow);
const DATE_MAX = isoDate(fourWeeks);

const initialForm = {
  channel: 'rueckruf',
  name: '',
  email: '',
  contact: '',
  region: '',
  window: CHANNELS.rueckruf.timeOptions[0],
  date: '', // nur für Zoom
  message: '',
  consent: false,
  website: '', // Honeypot – muss leer bleiben, für Menschen unsichtbar
};

export default function Process() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const channelCfg = CHANNELS[form.channel];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleChannel = (channelId) => {
    setForm((f) => ({
      ...f,
      channel: channelId,
      // Rückruf/WhatsApp: Default-Fenster. Zoom: leer, damit der User bewusst
      // einen konkreten Slot wählen muss (zusammen mit dem Wunsch-Tag).
      window: channelId === 'zoom' ? '' : CHANNELS[channelId].timeOptions[0],
      date: channelId === 'zoom' ? f.date : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setError('');
    setSending(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Senden fehlgeschlagen.');
      }

      navigate(ROUTES.danke);
    } catch (err) {
      setError(err.message || 'Netzwerkfehler. Bitte später erneut versuchen.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id={SECTION.prozess}
      className="process"
      aria-labelledby="process-heading"
    >
      <div className="process__inner">
        <div className="process__grid">
          {/* ---------- LEFT: Steps ---------- */}
          <div className="process__left">
            <span className="eyebrow">So funktioniert's</span>
            <h2 id="process-heading" className="process__heading">
              In 6 Schritten zu Ihrer{' '}
              <span className="process__heading-accent">Off-Market-Immobilie</span>.
            </h2>

            <ol className="steps">
              {STEPS.map((s, i) => (
                <li className="step" key={i}>
                  <div className="step__num">
                    <span>{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="step__body">
                    <h3 className="step__title">{s.title}</h3>
                    <p className="step__text">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* ---------- RIGHT: Call-Booking Form ---------- */}
          <aside
            id={SECTION.kontakt}
            className="process__right"
            aria-labelledby="form-heading"
          >
            <div className="form-card">
              <div className="form-card__head">
                <span className="form-card__badge">
                  <span className="form-card__badge-dot" />
                  {channelCfg.tagline} · kostenlos
                </span>
                <h3 id="form-heading" className="form-card__title">
                  {channelCfg.titleHtml}
                </h3>
                <p className="form-card__sub">{channelCfg.sub}</p>
              </div>

              {/* Kanal-Segmented-Control */}
              <div
                className="channels"
                role="tablist"
                aria-label="Bevorzugter Kontakt-Kanal"
              >
                {Object.values(CHANNELS).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="tab"
                    aria-selected={form.channel === c.id}
                    className={`channel ${form.channel === c.id ? 'is-active' : ''}`}
                    onClick={() => handleChannel(c.id)}
                  >
                    <span className="channel__icon">{c.icon}</span>
                    <span className="channel__label">{c.label}</span>
                  </button>
                ))}
              </div>

              <form className="form" onSubmit={handleSubmit} noValidate>
                {/* Honeypot – für Menschen unsichtbar, Bots füllen's aus */}
                <div className="form__honeypot" aria-hidden="true">
                  <label>
                    Website
                    <input
                      type="text"
                      name="website"
                      tabIndex="-1"
                      autoComplete="off"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </label>
                </div>

                <div className="form__row form__row--2">
                  <Field
                    label="Ihr Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                  <Field
                    label="E-Mail"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form__row form__row--2">
                  <Field
                    label={channelCfg.contactLabel}
                    name="contact"
                    type="tel"
                    value={form.contact}
                    onChange={handleChange}
                    placeholder={channelCfg.contactPlaceholder}
                    required={form.channel !== 'zoom'}
                  />
                  <Field
                    label="Wunsch-Region"
                    name="region"
                    value={form.region}
                    onChange={handleChange}
                    placeholder="z.B. Wien 19, Mallorca Ost, …"
                    required
                  />
                </div>

                {form.channel === 'zoom' ? (
                  /* Zoom: konkreter Wunsch-Tag via Kalender-Picker + Uhrzeit-Slot */
                  <div className="form__row form__row--2">
                    <Field
                      label="Wunsch-Tag"
                      name="date"
                      type="date"
                      value={form.date}
                      onChange={handleChange}
                      min={DATE_MIN}
                      max={DATE_MAX}
                      required
                    />
                    <label className="field">
                      <span className="field__label">
                        Uhrzeit
                        <span className="field__required" aria-hidden="true">*</span>
                      </span>
                      <select
                        className="field__control"
                        name="window"
                        value={form.window}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Bitte wählen …</option>
                        {channelCfg.timeOptions.map((opt) => (
                          <option key={opt}>{opt}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : (
                  /* Rückruf / WhatsApp: loses Zeitfenster reicht */
                  <div className="form__row">
                    <label className="field">
                      <span className="field__label">{channelCfg.timeLabel}</span>
                      <select
                        className="field__control"
                        name="window"
                        value={form.window}
                        onChange={handleChange}
                      >
                        {channelCfg.timeOptions.map((opt) => (
                          <option key={opt}>{opt}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}

                <div className="form__row">
                  <label className="field">
                    <span className="field__label">
                      Kurz beschreiben, was Sie suchen{' '}
                      <span className="field__hint">(optional)</span>
                    </span>
                    <textarea
                      className="field__control field__control--area"
                      name="message"
                      rows="3"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Budget, Objekttyp, Timing, Besonderheiten …"
                    />
                  </label>
                </div>

                <label className="form__consent">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={handleChange}
                    required
                  />
                  <span>
                    Ich bin einverstanden, dass meine Angaben zum Zweck der
                    Rückmeldung gespeichert werden. Kein Newsletter, keine
                    Weitergabe.
                  </span>
                </label>

                {error && (
                  <div className="form__error" role="alert">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-primary form__submit"
                  disabled={sending || !form.consent}
                >
                  {sending ? 'Wird gesendet …' : channelCfg.submitLabel}
                  {!sending && (
                    <svg
                      className="arrow"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="13 6 19 12 13 18" />
                    </svg>
                  )}
                </button>

                <p className="form__note">
                  100 % diskret · keine Makler, keine Datenweitergabe
                </p>
              </form>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

/* ---------- kleines Field-Atom ---------- */
function Field({
  label,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  min,
  max,
}) {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        {required && <span className="field__required" aria-hidden="true">*</span>}
      </span>
      <input
        className="field__control"
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        min={min}
        max={max}
        autoComplete="on"
      />
    </label>
  );
}
